// A broken yolk spreading over the egg white: shallow water on a height field
// with virtual pipes, the method behind many game water and erosion tools.
// Outflow fluxes with max(0, f) and the scaling factor K follow Mei, Decaudin &
// Hu (2007); pipes to all eight neighbours follow O'Brien & Hodgins (1995). Pipe fluxes decay
// quickly and a yield slope stops the flow, so it creeps like thick
// yolk and comes to rest as a puddle instead of thinning out forever. The
// diagonal pipes make the puddle spread round rather than as a diamond.
// Gathering adds a well under the yolk's home, and the yolk flows back.
// Water uses the same pipes with no yield and a hold depth: it moves onto dry
// floor only from cells deeper than that, so a puddle stops at the thickness
// that surface tension would hold instead of spreading to a film. Drag grows
// as the water thins (a viscous film: friction ~ 1 / depth^2), so the rush of
// a splash slows to a stop near the hold depth.
const OFFSETS=[[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[1,-1],[-1,1],[1,1]],OPPOSITE=[1,0,3,2,7,6,5,4];
// Axial and diagonal pipe weights in the ratio of the isotropic nine-point Laplacian.
const WEIGHTS=[1,1,1,1,.25,.25,.25,.25],DISTANCE=[1,1,1,1,Math.SQRT2,Math.SQRT2,Math.SQRT2,Math.SQRT2];
export class YolkFlow{
  // terrain: top height of the white per cell (nx * nz); mask: 1 where yolk may flow.
  // yieldSlope: the yolk stops on surfaces flatter than this (rise over run).
  // holdDepth: per cell or one value; 0 lets the flow wet dry cells freely.
  constructor({nx,nz,min,max,terrain,mask,gravity=6,damping=3.5,yieldSlope=.32,holdDepth=0,filmDrag=0}){
    this.nx=nx;this.nz=nz;this.min=[...min];this.max=[...max];
    this.dx=(max[0]-min[0])/nx;this.dz=(max[1]-min[1])/nz;this.area=this.dx*this.dz;
    this.terrain=Float32Array.from(terrain);this.mask=Uint8Array.from(mask);
    const n=nx*nz;this.water=new Float32Array(n);this.flux=new Float32Array(n*8);this.well=new Float32Array(n);
    this.hold=typeof holdDepth==='number'?new Float32Array(n).fill(holdDepth):Float32Array.from(holdDepth);this.filmDrag=filmDrag;
    this.gravity=gravity;this.damping=damping;this.yieldSlope=yieldSlope;this.activity=0;this.gathering=0;this.gatherCenter=null;
  }
  cell(x,z){return [Math.floor((x-this.min[0])/this.dx),Math.floor((z-this.min[1])/this.dz)];}
  centre(i,j){return [this.min[0]+(i+.5)*this.dx,this.min[1]+(j+.5)*this.dz];}
  // Drop a dome of yolk: height * sqrt(1 - (d / radius)^2).
  deposit(center,radius,height){
    for(let j=0;j<this.nz;j++)for(let i=0;i<this.nx;i++){
      const q=i+j*this.nx;if(!this.mask[q])continue;
      const [x,z]=this.centre(i,j),d=Math.hypot(x-center[0],z-center[1])/radius;
      if(d<1)this.water[q]+=height*Math.sqrt(1-d*d);
    }
    this.activity=1;
  }
  get volume(){let v=0;for(let q=0;q<this.water.length;q++)v+=this.water[q];return v*this.area;}
  volumeWithin(center,radius){
    let v=0;
    for(let j=0;j<this.nz;j++)for(let i=0;i<this.nx;i++){const [x,z]=this.centre(i,j);if(Math.hypot(x-center[0],z-center[1])<radius)v+=this.water[i+j*this.nx];}
    return v*this.area;
  }
  gather(center,radius){this.gathering=1e-6;this.gatherCenter=[...center];this.gatherRadius=radius;this.activity=1;}
  stopGathering(){this.gathering=0;this.gatherCenter=null;this.well.fill(0);}
  clear(){this.water.fill(0);this.flux.fill(0);this.stopGathering();this.activity=0;}
  // A drop lands: its water joins the cell under it, or a small dome of the
  // given radius so it lands round. Returns false off the floor.
  pour(x,z,volume,radius=0){
    const [i,j]=this.cell(x,z);if(i<0||j<0||i>=this.nx||j>=this.nz||!this.mask[i+j*this.nx])return false;
    this.activity=1;
    if(!(radius>0)){this.water[i+j*this.nx]+=volume/this.area;return true;}
    const ri=Math.ceil(radius/this.dx),rj=Math.ceil(radius/this.dz),cells=[];let sum=0;
    for(let b=Math.max(0,j-rj);b<=Math.min(this.nz-1,j+rj);b++)for(let a=Math.max(0,i-ri);a<=Math.min(this.nx-1,i+ri);a++){
      const q=a+b*this.nx;if(!this.mask[q])continue;
      const [cx,cz]=this.centre(a,b),d=Math.hypot(cx-x,cz-z)/radius;if(d<1){const w=Math.sqrt(1-d*d);cells.push(q,w);sum+=w;}
    }
    if(!sum){this.water[i+j*this.nx]+=volume/this.area;return true;}
    for(let n=0;n<cells.length;n+=2)this.water[cells[n]]+=volume*cells[n+1]/sum/this.area;
    return true;
  }
  // A finger drags yolk along with it.
  push(point,motion,radius){
    const length=Math.hypot(motion[0],motion[1]);if(length<1e-6)return;
    const shift=[motion[0]/length,motion[1]/length],amount=Math.min(.6,length/Math.min(this.dx,this.dz));
    const moved=new Map();
    for(let j=0;j<this.nz;j++)for(let i=0;i<this.nx;i++){
      const q=i+j*this.nx;if(!this.water[q])continue;
      const [x,z]=this.centre(i,j);if(Math.hypot(x-point[0],z-point[1])>radius)continue;
      const a=i+Math.round(shift[0]),b=j+Math.round(shift[1]);if(a<0||b<0||a>=this.nx||b>=this.nz||!this.mask[a+b*this.nx])continue;
      const take=this.water[q]*amount;this.water[q]-=take;moved.set(a+b*this.nx,(moved.get(a+b*this.nx)??0)+take);
    }
    for(const [q,v] of moved)this.water[q]+=v;
    this.activity=1;
  }
  step(dt){
    if(!(dt>0)||(!this.activity&&!this.gathering))return;
    const steps=Math.max(1,Math.ceil(dt/(1/120))),h=dt/steps,{nx,nz,water:w,terrain:t,mask,flux:f,well}=this;
    if(this.gathering){
      this.gathering+=dt;
      const c=this.gatherCenter,depth=Math.min(1,this.gathering/.6)*1.4;
      for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){const [x,z]=this.centre(i,j),d=Math.hypot(x-c[0],z-c[1]);well[i+j*nx]=-depth*Math.max(0,1-d/(this.gatherRadius*4));}
    }
    const damping=Math.exp(-(this.gathering?this.damping*1.8:this.damping)*h),g=this.gravity,slope=this.gathering?0:this.yieldSlope,hold=this.hold,drag=this.filmDrag;
    let motion=0;const width=(this.dx+this.dz)/2;
    for(let s=0;s<steps;s++){
      for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){
        const q=i+j*nx,o=q*8;if(!mask[q]){for(let e=0;e<8;e++)f[o+e]=0;continue;}
        const level=t[q]+w[q]+well[q];let total=0;
        for(let e=0;e<8;e++){
          const a=i+OFFSETS[e][0],b=j+OFFSETS[e][1];let value=f[o+e]*damping;
          if(a<0||b<0||a>=nx||b>=nz||!mask[a+b*nx])value=0;
          // A shallow edge cannot wet the dry floor beyond it.
          else if(!w[a+b*nx]&&w[q]<hold[q])value=0;
          else{
            const n=a+b*nx,drop=level-(t[n]+w[n]+well[n]),stop=slope*DISTANCE[e]*width;if(drop>stop)value+=h*g*(drop-stop)*width*WEIGHTS[e];
            if(drag){const d=Math.max(hold[q]*.1,(w[q]+w[n])/2);value/=1+drag*h*(hold[q]/d)**2;}
          }
          f[o+e]=value>0?value:0;total+=f[o+e];
        }
        // Never send more than the cell holds.
        if(total>0){const k=Math.min(1,w[q]*this.area/(total*h));if(k<1)for(let e=0;e<8;e++)f[o+e]*=k;}
      }
      for(let j=0;j<nz;j++)for(let i=0;i<nx;i++){
        const q=i+j*nx;if(!mask[q])continue;
        let inflow=0,out=0;
        for(let e=0;e<8;e++){
          out+=f[q*8+e];const a=i+OFFSETS[e][0],b=j+OFFSETS[e][1];
          if(a>=0&&b>=0&&a<nx&&b<nz)inflow+=f[(a+b*nx)*8+OPPOSITE[e]];
        }
        const change=h*(inflow-out)/this.area;
        w[q]=Math.max(0,w[q]+change);motion=Math.max(motion,Math.abs(change));
      }
    }
    this.activity=motion>1e-6||this.gathering?1:0;
    if(!this.activity)this.flux.fill(0);
  }
}
